import { useMemo } from "react";
import { useBranding } from "@/hooks/useBranding";

/**
 * BuiltBuddy chat widget persona — org-wide avatar + greeting (Settings › Chat widget, admins only).
 * Mirrors CHAT_WIDGET_DEFAULTS in backend/org_branding.py; the API merges the saved values over these.
 */
export const CHAT_WIDGET_DEFAULTS = Object.freeze({
  avatar_style: "engineer",
  greeting_name: "Buddy",
  greeting_role: "engineer",
  greeting_enabled: true,
});

export const AVATAR_STYLES = [
  { key: "engineer", label: "Engineer", blurb: "Steel-blue hard hat and a roll of drawings — the project engineer who reads the set." },
  { key: "builder", label: "Builder", blurb: "Site hat, tool belt and a hammer on the swing — hands-on and practical." },
  { key: "assistant", label: "Personal assistant", blurb: "Headset and a live chat badge — a clean, professional front desk." },
];

export const isAvatarStyle = (s) => AVATAR_STYLES.some((a) => a.key === s);

/** "Hi, I'm Buddy — your personal engineer." (same wording the API returns as greeting_text). */
export const greetingTextFor = (cw = {}) => {
  const name = String(cw.greeting_name || "").trim() || CHAT_WIDGET_DEFAULTS.greeting_name;
  const role = String(cw.greeting_role || "").trim() || CHAT_WIDGET_DEFAULTS.greeting_role;
  return `Hi, I'm ${name} \u2014 your personal ${role}.`;
};

/** Defaults merged with whatever the API returned (partial objects are fine — the Settings preview passes drafts). */
export const resolveChatWidget = (cw) => {
  const merged = { ...CHAT_WIDGET_DEFAULTS, ...(cw || {}) };
  if (!isAvatarStyle(merged.avatar_style)) merged.avatar_style = CHAT_WIDGET_DEFAULTS.avatar_style;
  if (!String(merged.greeting_name || "").trim()) merged.greeting_name = CHAT_WIDGET_DEFAULTS.greeting_name;
  if (!String(merged.greeting_role || "").trim()) merged.greeting_role = CHAT_WIDGET_DEFAULTS.greeting_role;
  merged.greeting_enabled = merged.greeting_enabled !== false;
  merged.greeting_text = greetingTextFor(merged);
  return merged;
};

/** The signed-in organisation's widget persona — rides on the shared branding fetch (GET /api/org). */
export const useChatWidget = () => {
  const { branding } = useBranding();
  const raw = branding?.chat_widget;
  return useMemo(() => resolveChatWidget(raw), [raw]);
};
